import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, FileText, Image as ImageIcon, Trash2, Plus, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { toastApiError } from "../lib/upgrade";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export const CONTACT_KINDS = [
  { v: "guest", l: "Guests" },
  { v: "client", l: "Clients" },
  { v: "planner", l: "Planners" },
  { v: "vendor", l: "Vendors" },
];
const COLS = [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["role", "Role / group"], ["notes", "Notes"]];
const BLANK = { name: "", email: "", phone: "", company: "", role: "", notes: "" };

function Dropzone({ onFile, busy }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="import-dropzone"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); e.dataTransfer.files[0] && onFile(e.dataTransfer.files[0]); }}
      className={`w-full rounded-3xl border-2 border-dashed p-8 md:p-10 text-center transition-colors ${over ? "border-[var(--gold)] bg-[#C8A96B]/10" : "border-[var(--line)] hover:border-[var(--gold)]"}`}>
      {busy ? <Loader2 className="w-10 h-10 mx-auto text-[var(--gold)] animate-spin" /> : <UploadCloud className="w-10 h-10 mx-auto text-[var(--gold)]" />}
      <p className="font-serif text-xl mt-3">{busy ? "Reading your file…" : "Drop a file or tap to upload"}</p>
      <div className="flex justify-center gap-4 mt-3 text-xs text-[var(--muted)]">
        <span className="flex items-center gap-1"><FileSpreadsheet className="w-4 h-4" /> CSV / Excel</span>
        <span className="flex items-center gap-1"><FileText className="w-4 h-4" /> PDF</span>
        <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> Photo</span>
      </div>
      <p className="text-[11px] text-[var(--muted)] mt-2 flex items-center justify-center gap-1"><Sparkles className="w-3 h-3 text-[var(--gold)]" /> PDFs and photos are read by AI. You'll review everything before saving.</p>
      <input ref={ref} type="file" className="hidden" data-testid="import-file-input" accept=".csv,.xlsx,.xls,.pdf,image/*" onChange={(e) => { e.target.files[0] && onFile(e.target.files[0]); e.target.value = ""; }} />
    </button>
  );
}

function PreviewTable({ rows, setRows }) {
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="max-h-[45vh] overflow-auto rounded-2xl border border-[var(--line)]" data-testid="import-preview">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-[var(--bg-warm)] sticky top-0"><tr>{COLS.map(([, l]) => <th key={l} className="text-left font-normal text-[11px] uppercase tracking-[0.14em] px-2 py-2">{l}</th>)}<th /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[var(--line)]" data-testid={`import-row-${i}`}>
              {COLS.map(([k]) => <td key={k} className={`px-1 py-1 ${k === "email" ? "min-w-[200px]" : ""}`}><input value={r[k] || ""} title={r[k] || ""} onChange={(e) => set(i, k, e.target.value)} data-testid={`import-cell-${i}-${k}`} className="w-full bg-transparent px-1.5 py-1 rounded focus:bg-white focus:ring-1 focus:ring-[var(--gold)] outline-none" /></td>)}
              <td className="px-1"><button onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`import-row-delete-${i}`} className="p-1.5 text-[var(--muted)] hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BulkImportDialog({ open, onClose, defaultKind = "guest", onSaved, hasWedding = false }) {
  const [kind, setKind] = useState(defaultKind);
  const [rows, setRows] = useState(null);
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);
  const [toGuests, setToGuests] = useState(true);
  useEffect(() => { if (open) setKind(defaultKind); }, [open, defaultKind]);

  const reset = () => { setRows(null); setMethod(""); };
  async function parse(file) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setRows(data.rows); setMethod(data.method);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function save() {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { kind, rows, add_to_guest_list: toGuests });
      toast.success(`Saved ${data.saved} ${kind}s${data.guests_added ? ` · ${data.guests_added} added to your guest list` : ""}`);
      reset(); onSaved?.(data); onClose();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-4xl" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle className="font-serif font-light text-3xl">Bulk upload</DialogTitle></DialogHeader>
        <div className="flex gap-1 flex-wrap" data-testid="import-kind">
          {CONTACT_KINDS.map((k) => <button key={k.v} onClick={() => setKind(k.v)} data-testid={`import-kind-${k.v}`} className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] border ${kind === k.v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)]"}`}>{k.l}</button>)}
        </div>
        {!rows ? <Dropzone onFile={parse} busy={busy} /> : (
          <>
            <p className="text-sm text-[var(--muted)]" data-testid="import-found">Found <b className="text-[var(--ink)]">{rows.length}</b> {rows.length === 1 ? "person" : "people"}{method === "ai" && " with AI. Please check spelling before saving"}.</p>
            <PreviewTable rows={rows} setRows={setRows} />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-3 items-center">
                <button onClick={() => setRows([...rows, { ...BLANK }])} className="text-sm text-[var(--gold)] flex items-center gap-1" data-testid="import-add-row"><Plus className="w-4 h-4" /> Add row</button>
                <button onClick={reset} className="text-sm text-[var(--muted)]" data-testid="import-start-over">Upload a different file</button>
              </div>
              {kind === "guest" && hasWedding && (
                <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={toGuests} onChange={(e) => setToGuests(e.target.checked)} data-testid="import-to-guestlist" className="accent-[var(--gold)]" /> Also add to my wedding guest list</label>
              )}
            </div>
            <button onClick={save} disabled={busy || !rows.length} className="btn-gold w-full justify-center" data-testid="import-save">{busy ? "Saving…" : `Save ${rows.length} ${CONTACT_KINDS.find((k) => k.v === kind).l.toLowerCase()}`}</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
